import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Paper, Grid,
  Avatar, IconButton
} from '@mui/material';
import { UserRound, Plus, Pencil } from 'lucide-react';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import SpeechAnalysisModal from './SpeechAnalysisModal';

export default function CharactersPanel({ getToken }) {
  const { activeTeamId } = useTeam();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedCount, setCachedCount] = useCachedPanelCount(activeTeamId ? `characters:${activeTeamId}` : null, 6);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [formPreview, setFormPreview] = useState(null);
  const [formStyleInstruction, setFormStyleInstruction] = useState('');
  const [saving, setSaving] = useState(false);
  const [analysisChar, setAnalysisChar] = useState(null);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const load = useCallback(async () => {
    if (!activeTeamId) return;
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/characters?teamId=${encodeURIComponent(activeTeamId)}`, { headers: h });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      const list = data.characters || [];
      setCharacters(list);
      setCachedCount(list.length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingChar(null);
    setFormName('');
    setFormDesc('');
    setFormStyleInstruction('');
    setFormFile(null);
    setFormPreview(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditingChar(c);
    setFormName(c.name);
    setFormDesc(c.description || '');
    setFormStyleInstruction(c.styleInstruction || '');
    setFormFile(null);
    setFormPreview(c.avatar?.url128 || c.avatar?.url512 || null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingChar(null);
    setSaving(false);
    setFormFile(null);
    setFormPreview(null);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFormFile(f);
    setFormPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('teamId', activeTeamId);
      fd.append('name', formName.trim());
      fd.append('description', formDesc.trim());
      fd.append('styleInstruction', formStyleInstruction.trim());
      if (formFile) fd.append('avatar', formFile);

      const url = editingChar
        ? `${API_BASE_URL}/api/characters/${editingChar.id}`
        : `${API_BASE_URL}/api/characters`;
      const method = editingChar ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Failed to save');
      closeModal();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteChar = async (c) => {
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/characters/${c.id}`, { method: 'DELETE', headers: h });
      if (!res.ok) throw new Error('Failed to delete');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ color: 'warning.light', display: 'flex', alignItems: 'center' }}>
            <UserRound size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Characters
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={openCreate}>
          Add character
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? <AdminPanelSkeleton variant="grid" count={cachedCount} /> : characters.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">No characters yet.</Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {characters.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <Paper
                variant="outlined"
                onClick={() => { setAnalysisChar(c); setAnalysisOpen(true); }}
                sx={{
                  p: 2,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(139,92,246,0.4)', bgcolor: 'rgba(139,92,246,0.04)' }
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    src={c.avatar?.url128 || c.avatar?.url64 || undefined}
                    sx={{ width: 48, height: 48, bgcolor: 'rgba(139, 92, 246, 0.14)', color: 'primary.light' }}
                  >
                    {c.name[0]?.toUpperCase()}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>{c.name}</Typography>
                    {c.description && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {c.description}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => openEdit(c)} sx={{ color: 'text.secondary' }}>
                      <Pencil size={14} />
                    </IconButton>
                    <ConfirmDeleteButton onConfirm={() => deleteChar(c)} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <AdminModal
        open={modalOpen}
        onClose={closeModal}
        title={editingChar ? 'Edit character' : 'New character'}
        maxWidth="xs"
        actions={
          <>
            <Button onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!formName.trim() || saving}>
              {saving ? 'Saving...' : editingChar ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} alignItems="center">
          <Avatar
            src={formPreview || undefined}
            sx={{ width: 96, height: 96, cursor: 'pointer', bgcolor: 'rgba(139, 92, 246, 0.14)', color: 'primary.light', fontSize: '2rem' }}
            onClick={() => fileRef.current?.click()}
          >
            {formName ? formName[0]?.toUpperCase() : '?'}
          </Avatar>
          <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()}>
            {formPreview ? 'Change avatar' : 'Upload avatar'}
          </Button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
          <TextField label="Name" size="small" fullWidth value={formName} onChange={(e) => setFormName(e.target.value)} autoFocus />
          <TextField label="Description" size="small" fullWidth multiline rows={2} value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
          <TextField
            label="Communication style instructions"
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={12}
            value={formStyleInstruction}
            onChange={(e) => setFormStyleInstruction(e.target.value)}
            placeholder="Describe how this character speaks, their tone, mannerisms..."
          />
        </Box>
      </AdminModal>

      <SpeechAnalysisModal
        open={analysisOpen}
        onClose={() => { setAnalysisOpen(false); setAnalysisChar(null); }}
        character={analysisChar}
        getToken={getToken}
        onCharacterUpdate={load}
      />
    </Box>
  );
}
